import { useEffect, useState } from 'react';
import type { Api } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote, SaveBar, Section, useDraft } from '../ui';
import { useSpecSave, type EditableSpec } from './AgentSettings';

type Pace = 'relaxed' | 'normal' | 'quick';
const lines = (x: string): string[] => x.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 5);

/** Conversation tab: how patiently the agent waits (pace) and its tone for questions it cannot answer. */
export const ConversationPanel = ({ api, t, agentId, spec, onSaved }: { api: Api; t: T; agentId: string; spec: EditableSpec; onSaved: () => void }) => {
  // ADR-071: without the agents permission the choices are shown read-only.
  const canEdit = useCan()(PERM.agents);
  const initialGreeting = ((spec as { greeting?: { lines: string[] } }).greeting?.lines ?? []).join('\n');
  const d = useDraft({
    pace: ((spec.pace as Pace | undefined) ?? 'normal') as Pace,
    humor: (spec.style.humor ?? 'light') as 'light' | 'off',
    // ADR-042: open with a short greeting only (the introduction follows the customer's reply).
    greet: initialGreeting ? 'greet' as const : 'full' as const,
    greeting: initialGreeting,
  });
  const [suggestions, setSuggestions] = useState<string[]>([]);
  useEffect(() => {
    // The suggestion lookup needs the agents permission; asking without it would only be refused (and logged).
    if (!canEdit) return;
    void api.greetingSuggestions(agentId).then((r) => setSuggestions(r.lines)).catch(() => undefined);
  }, [api, agentId, canEdit]);
  const s = useSpecSave(api, agentId, onSaved, t);
  const v = d.value;
  return (
    <div className="settings">
      <fieldset disabled={!canEdit} className="readonly-fieldset">
        <ReadOnlyNote t={t} need={PERM.agents} />
        <Section title={t('pace.title')} description={t('pace.hint')}>
          <div className="choice-row">
            {(['relaxed', 'normal', 'quick'] as const).map((p) => (
              <label key={p} className={`choice ${v.pace === p ? 'on' : ''}`}>
                <input type="radio" name="pace" checked={v.pace === p} onChange={() => d.set({ ...v, pace: p })} />
                <span>{t(`pace.${p}`)}<small>{t(`pace.${p}.hint`)}</small></span>
              </label>
            ))}
          </div>
        </Section>
        <Section title={t('greeting.title')} description={t('greeting.hint')}>
          <div className="choice-row">
            {(['full', 'greet'] as const).map((g) => (
              <label key={g} className={`choice ${v.greet === g ? 'on' : ''}`}>
                <input type="radio" name="greet" checked={v.greet === g} onChange={() => d.set({ ...v, greet: g })} />
                <span>{t(`greeting.${g}`)}<small>{t(`greeting.${g}.hint`)}</small></span>
              </label>
            ))}
          </div>
          {v.greet === 'greet' && (
            <div className="greeting-edit">
              <label>{t('greeting.lines')}
                <textarea rows={4} maxLength={600} value={v.greeting} placeholder={suggestions[0] ?? ''} onChange={(e) => d.set({ ...v, greeting: e.target.value })} />
              </label>
              <span className="hint">{t('greeting.lines.hint')}</span>
              {canEdit && suggestions.length > 0 && (
                <div className="greet-chips">
                  <span className="muted small">{t('greeting.suggestions')}</span>
                  {suggestions.map((s) => (
                    <button key={s} type="button" className="greet-chip" disabled={lines(v.greeting).includes(s) || lines(v.greeting).length >= 5}
                      onClick={() => d.set({ ...v, greeting: [...lines(v.greeting), s].join('\n') })}>+ {s}</button>
                  ))}
                </div>
              )}
              <p className="hint">{t('greeting.legal')}</p>
            </div>
          )}
        </Section>
        <Section title={t('humor.title')} description={t('humor.hint')}>
          <div className="choice-row">
            {(['light', 'off'] as const).map((h) => (
              <label key={h} className={`choice ${v.humor === h ? 'on' : ''}`}>
                <input type="radio" name="humor" checked={v.humor === h} onChange={() => d.set({ ...v, humor: h })} />
                <span>{t(`humor.${h}`)}<small>{t(`humor.${h}.hint`)}</small></span>
              </label>
            ))}
          </div>
        </Section>
      </fieldset>
      <SaveBar t={t} need={PERM.agents} dirty={d.dirty} saving={s.saving} error={s.error} onReset={d.reset} onSave={() => void s.save({ pace: v.pace, style: { humor: v.humor }, greeting: v.greet === 'greet' && lines(v.greeting).length ? { lines: lines(v.greeting) } : null }, d.commit)} />
    </div>
  );
};
